// useFetchTestimonials.ts
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { testimonialsData, TestimonialsType } from "../components/constants/testimonialsData";
import { RawReview } from "./useFetchReview";
import { capitalizeWords } from "../components/utils";
export type ApiResponse = {
    status: "success" | "error";
    data: RawReview[];
};

const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

// Transformer: maps RawReview to TestimonialsType
const transformToTestimonial = (raw: RawReview): TestimonialsType => ({
    name: capitalizeWords(raw.user.name),
    reviewType: mapReviewType(raw.review_type),
    image: raw.user.profile_picture_url,
    comment: raw.review,
});

// Maps number to string literal
const mapReviewType = (type: number): TestimonialsType["reviewType"] => {
    switch (type) {
        case 1:
            return "positive";
        case 2:
            return "neutral";
        case 3:
            return "negative";
        default:
            return "neutral";
    }
};

// Fetch function
const fetchTestimonials = async (location: string): Promise<TestimonialsType[]> => {
    if (isDemoMode) {
        return testimonialsData
    }
    const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}api/v1/custom/company-reviews/${location}`); // Change URL
    const json: ApiResponse = await res.json();
    if (json.status !== "success") throw new Error("Failed to fetch testimonials");
    return json.data.map(transformToTestimonial);
};

// Main query hook
export const useFetchTestimonials = (location: string) => {
    return useQuery<TestimonialsType[]>({
        queryKey: ["testimonials"],
        queryFn: () => fetchTestimonials(location),
        staleTime: Infinity,
    });
};

// Prefetch hook (e.g. in server or layout)
export const prefetchTestimonials = async (queryClient: ReturnType<typeof useQueryClient>, location: string) => {
    await queryClient.prefetchQuery({
        queryKey: ["testimonials"],
        queryFn: () => fetchTestimonials(location),
        staleTime: Infinity,
    });
};
